import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChatIcon from "@mui/icons-material/Chat";
import GroupIcon from "@mui/icons-material/Group";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  Button,
  Card,
  CardContent,
  Fade,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useWhitelabel } from "../../../context/WhitelabelContext";
import { getSupportContactInfo } from "../../../utils";
import { useEmployeeTerminology } from "../../../utils/terminology";

const CompletionContainer = styled(Box)(({ theme }) => ({
  width: "100%",
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: "100%",
  transition: "all 0.3s ease",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
  },
}));

const getNextSteps = (employeesLower: string) => [
  {
    icon: ChatIcon,
    title: "Start Chatting",
    description: `Begin conversations with your AI ${employeesLower} to get work done faster`,
    color: "primary.main",
  },
  {
    icon: GroupIcon,
    title: "Invite Your Team",
    description: "Add colleagues to collaborate and share AI-powered workflows",
    color: "secondary.main",
  },
  {
    icon: AutoAwesomeIcon,
    title: "Create Content",
    description:
      "Generate marketing content, blog posts, and social media updates",
    color: "warning.main",
  },
  {
    icon: TrendingUpIcon,
    title: "Analyze & Optimize",
    description:
      "Use AI insights to improve your business processes and decisions",
    color: "success.main",
  },
];

interface CompletionStepProps {
  appName: string;
  companyName: string;
  selectedPlan?: string;
  onComplete: () => void;
}

// Styled container for custom HTML content
const CustomContentContainer = styled(Box)(({ theme }) => ({
  "& h1": {
    ...theme.typography.h4,
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    marginTop: 0,
    color: theme.palette.text.primary,
  },
  "& h2": {
    ...theme.typography.h5,
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
    color: theme.palette.text.primary,
  },
  "& h3": {
    ...theme.typography.h6,
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  "& p": {
    ...theme.typography.body1,
    marginBottom: theme.spacing(2),
    marginTop: 0,
    lineHeight: 1.7,
    color: theme.palette.text.secondary,
  },
  "& ul, & ol": {
    marginBottom: theme.spacing(2),
    marginTop: 0,
    paddingLeft: theme.spacing(3),
  },
  "& li": {
    ...theme.typography.body1,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  "& a": {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  "& strong, & b": {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  "& em, & i": {
    fontStyle: "italic",
  },
  "& blockquote": {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(2),
    marginLeft: 0,
    marginRight: 0,
    marginBottom: theme.spacing(2),
    fontStyle: "italic",
    color: theme.palette.text.secondary,
  },
  "& hr": {
    border: "none",
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(3, 0),
  },
}));

export default function CompletionStep({
  appName,
  companyName,
  selectedPlan = "free",
  onComplete,
}: CompletionStepProps) {
  const { employeesLower } = useEmployeeTerminology();
  const [showConfetti, setShowConfetti] = useState(false);
  const theme = useTheme();
  const { whitelabelSettings } = useWhitelabel();

  useEffect(() => {
    // Show confetti effect
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Check if whitelabel has custom completion content
  const customContent = whitelabelSettings?.onboardingCompletionContent;

  // Replace placeholders in custom content
  const processCustomContent = (content: string): string => {
    return content
      .replace(/\{\{appName\}\}/g, appName)
      .replace(/\{\{companyName\}\}/g, companyName);
  };

  // Render custom content if available
  if (customContent) {
    return (
      <CompletionContainer>
        <Fade in={true} timeout={1000}>
          <Box>
            <CustomContentContainer
              dangerouslySetInnerHTML={{
                __html: processCustomContent(customContent),
              }}
            />

            <Button
              variant="contained"
              size="large"
              onClick={onComplete}
              startIcon={<RocketLaunchIcon />}
              sx={{
                mt: 4,
                px: 6,
                py: 2,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "1.1rem",
                borderRadius: 3,
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 8px 25px rgba(255, 255, 255, 0.1)"
                    : "0 8px 25px rgba(0, 0, 0, 0.15)",
                "&:hover": {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark} 90%)`,
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 12px 35px rgba(255, 255, 255, 0.15)"
                      : "0 12px 35px rgba(0, 0, 0, 0.2)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              Get Started with {appName}
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              Questions? Contact support at{" "}
              {getSupportContactInfo(whitelabelSettings)} anytime.
            </Typography>
          </Box>
        </Fade>
      </CompletionContainer>
    );
  }

  // Default completion page content
  return (
    <CompletionContainer>
      <Fade in={true} timeout={1000}>
        <Box>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "text.primary",
              }}
            >
              Welcome to {appName}!
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                mb: 1,
                fontWeight: 500,
              }}
            >
              {companyName} is all set up and ready to go!
            </Typography>
            {selectedPlan !== "free" && (
              <Typography
                variant="body1"
                color="primary"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  display: "inline-block",
                }}
              >
                {selectedPlan === "entrepreneur"
                  ? "Entrepreneur Plan"
                  : "Business Plan"}{" "}
                Selected
              </Typography>
            )}
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.6, maxWidth: 500 }}
            >
              Your AI-powered workspace is ready. Explore the possibilities and
              start transforming how your team works.
            </Typography>
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: "text.primary",
            }}
          >
            Here's what you can do next:
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {getNextSteps(employeesLower).map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Grid item xs={12} sm={6} key={index}>
                  <FeatureCard elevation={2}>
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          textAlign: "center",
                          height: "100%",
                        }}
                      >
                        <IconComponent
                          sx={{
                            fontSize: 48,
                            mb: 2,
                            color: step.color,
                          }}
                        />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: "text.primary",
                          }}
                        >
                          {step.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.5 }}
                        >
                          {step.description}
                        </Typography>
                      </Box>
                    </CardContent>
                  </FeatureCard>
                </Grid>
              );
            })}
          </Grid>

          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 4,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Pro Tip
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Start by exploring the Chat feature to see how AI employees can
              help with your daily tasks. You can always customize your settings
              and add more team members later!
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={onComplete}
            startIcon={<RocketLaunchIcon />}
            sx={{
              px: 6,
              py: 2,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1.1rem",
              borderRadius: 3,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 8px 25px rgba(255, 255, 255, 0.1)"
                  : "0 8px 25px rgba(0, 0, 0, 0.15)",
              "&:hover": {
                background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark} 90%)`,
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 12px 35px rgba(255, 255, 255, 0.15)"
                    : "0 12px 35px rgba(0, 0, 0, 0.2)",
                transform: "translateY(-2px)",
              },
            }}
          >
            Get Started with {appName}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Questions? Contact support at{" "}
            {getSupportContactInfo(whitelabelSettings)} anytime.
          </Typography>
        </Box>
      </Fade>
    </CompletionContainer>
  );
}
